<template>
  <div class="checkin-page app-page">
    <div class="checkin-container">
      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        <p>加载中...</p>
      </div>

      <div v-if="error" class="error-message">{{ error }}</div>

      <div v-if="!loading" class="checkin-content">
        <div class="checkin-status-card">
          <div class="streak-section">
            <div class="streak-icon">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor" opacity="0.9"><path d="M13.5 2L12 6l-1.5-4H9l1.5 5L9 9h3v11l5.5-9H13l3-5z"/><path d="M13.5 2L12 6l-1.5-4H9l1.5 5L9 9h3v11l5.5-9H13l3-5z" transform="translate(3,2)" opacity="0.4"/></svg>
            </div>
            <div class="streak-days">
              <span class="streak-value">{{ checkinInfo.continuousDays }}</span>
              <span class="streak-unit">天</span>
            </div>
            <span class="streak-label">连续签到</span>
          </div>

          <div class="checkin-progress">
            <div class="progress-header">
              <span class="progress-title">30天会员奖励进度</span>
              <span class="progress-percent">{{ Math.round((checkinInfo.continuousDays / 30) * 100) }}%</span>
            </div>
            <div class="progress-track">
              <div
                class="progress-fill"
                :style="{ width: Math.min((checkinInfo.continuousDays / 30) * 100, 100) + '%' }"
              ></div>
              <div class="progress-milestone" :style="{ left: '100%' }">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              </div>
            </div>
            <div class="progress-labels">
              <span class="progress-label">{{ checkinInfo.continuousDays }}/30 天</span>
              <span class="progress-reward">奖励：7天会员</span>
            </div>
          </div>

          <div class="checkin-actions">
            <button
              class="checkin-btn"
              @click="handleCheckin"
              :disabled="checkinInfo.checked"
            >
              <template v-if="checkinInfo.checked">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                今日已签到
              </template>
              <template v-else>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                立即签到
              </template>
            </button>

            <div v-if="checkinInfo.rewardAvailable" class="reward-section">
              <div class="reward-glow">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              </div>
              <p>恭喜您获得7天会员奖励！</p>
              <button class="claim-btn" @click="claimCheckinReward">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                立即领取
              </button>
            </div>
          </div>
        </div>

        <div class="calendar-section">
          <div class="month-nav">
            <button class="nav-btn" @click="changeMonth(-1)">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              <span>上月</span>
            </button>
            <h2 class="current-month">{{ checkinInfo.year }}年 {{ checkinInfo.month }}月</h2>
            <button class="nav-btn" @click="changeMonth(1)">
              <span>下月</span>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>

          <div class="calendar-grid">
            <div class="weekday" v-for="day in ['日', '一', '二', '三', '四', '五', '六']" :key="day">{{ day }}</div>

            <!-- 空白格子 -->
            <div class="date empty" v-for="empty in firstDayOffset" :key="'empty-' + empty"></div>

            <!-- 日期格子 -->
            <div
              class="date"
              v-for="date in daysInMonth"
              :key="date"
              :class="{
                'checked-in': isCheckedIn(date),
                'today': isToday(date),
                'future': isFutureDate(date)
              }"
            >
              <span class="date-number">{{ date }}</span>
              <span class="checkin-mark" v-if="isCheckedIn(date)">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
            </div>
          </div>

          <div class="calendar-stats">
            <div class="stat-item">
              <div class="stat-icon">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </div>
              <div class="stat-text">
                <span class="stat-label">本月签到</span>
                <span class="stat-value">{{ checkinInfo.monthCheckinCount }} 天</span>
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-icon">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              </div>
              <div class="stat-text">
                <span class="stat-label">连续签到</span>
                <span class="stat-value">{{ checkinInfo.continuousDays }} 天</span>
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-icon">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </div>
              <div class="stat-text">
                <span class="stat-label">签到率</span>
                <span class="stat-value">{{ calculateCheckinRate() }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import checkinApi from '@/api/checkin';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';

const router = useRouter();
const currentUserId = ref(localStorage.getItem('userId') ? Number(localStorage.getItem('userId')) : null);
const loading = ref(true);
const error = ref('');
const checkinInfo = ref({
  userId: null,
  checked: false,
  continuousDays: 0,
  monthCheckinCount: 0,
  rewardAvailable: false,
  dailyCheckinStatus: {},
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1
});

// 计算当月天数
const daysInMonth = computed(() => {
  return new Date(checkinInfo.value.year, checkinInfo.value.month, 0).getDate();
});

// 计算当月第一天是星期几（0-6）
const firstDayOffset = computed(() => {
  return new Date(checkinInfo.value.year, checkinInfo.value.month - 1, 1).getDay();
});

// 获取当前日期
const today = new Date();
const currentDate = {
  year: today.getFullYear(),
  month: today.getMonth() + 1,
  date: today.getDate()
};

// 检查是否是今天
const isToday = (date) => {
  return date === currentDate.date &&
         checkinInfo.value.month === currentDate.month &&
         checkinInfo.value.year === currentDate.year;
};

// 检查是否是未来日期
const isFutureDate = (date) => {
  if (checkinInfo.value.year > currentDate.year) return true;
  if (checkinInfo.value.year < currentDate.year) return false;
  if (checkinInfo.value.month > currentDate.month) return true;
  if (checkinInfo.value.month < currentDate.month) return false;
  return date > currentDate.date;
};

// 检查某天是否已签到
const isCheckedIn = (date) => {
  return checkinInfo.value.dailyCheckinStatus[date] === true;
};

// 计算签到率
const calculateCheckinRate = () => {
  const currentDateInMonth = checkinInfo.value.month === currentDate.month &&
                            checkinInfo.value.year === currentDate.year
                            ? currentDate.date
                            : daysInMonth.value;

  if (currentDateInMonth === 0) return 0;
  const rate = (checkinInfo.value.monthCheckinCount / currentDateInMonth) * 100;
  return Math.round(rate);
};

// 获取签到数据
const fetchCheckinData = async () => {
  if (!currentUserId.value) {
    ElMessage.error('请先登录');
    router.push('/login');
    return;
  }

  try {
    loading.value = true;
    error.value = '';

    const res = await checkinApi.getCheckinInfoByMonth(
      currentUserId.value,
      checkinInfo.value.year,
      checkinInfo.value.month
    );

    if (res.data.code == 200) {
      checkinInfo.value = res.data.data;
    } else {
      error.value = res.data.msg || '获取签到信息失败';
    }
  } catch (err) {
    error.value = err.response?.data?.msg || '获取签到信息失败';
  } finally {
    loading.value = false;
  }
};

// 执行签到
const handleCheckin = async () => {
  if (!currentUserId.value) return;

  try {
    loading.value = true;
    const res = await checkinApi.checkin(currentUserId.value);

    if (res.data.code == 200) {
      checkinInfo.value = res.data.data;
      ElMessage.success('签到成功！');
    } else {
      ElMessage.error(res.data.msg || '签到失败');
    }
  } catch (err) {
    ElMessage.error(err.response?.data?.msg || '签到失败');
  } finally {
    loading.value = false;
  }
};

// 领取奖励
const claimCheckinReward = async () => {
  if (!currentUserId.value) return;

  try {
    loading.value = true;
    const res = await checkinApi.claimReward(currentUserId.value);

    if (res.data.code == 200 && res.data.data) {
      ElMessageBox.alert('恭喜您获得7天会员奖励！', '领取成功', {
        confirmButtonText: '查看会员详情',
        callback: () => {
          router.push('/membership/detail');
        }
      });
      checkinInfo.value.rewardAvailable = false;
    } else {
      ElMessage.error(res.data.msg || '领取奖励失败');
    }
  } catch (err) {
    ElMessage.error(err.response?.data?.msg || '领取奖励失败');
  } finally {
    loading.value = false;
  }
};

// 切换月份
const changeMonth = (delta) => {
  let newMonth = checkinInfo.value.month + delta;
  let newYear = checkinInfo.value.year;

  if (newMonth > 12) {
    newMonth = 1;
    newYear++;
  } else if (newMonth < 1) {
    newMonth = 12;
    newYear--;
  }

  checkinInfo.value.month = newMonth;
  checkinInfo.value.year = newYear;

  fetchCheckinData();
};

// 监听年月变化重新获取数据
watch([() => checkinInfo.value.year, () => checkinInfo.value.month], () => {
  if (!loading.value) {
    fetchCheckinData();
  }
});

onMounted(() => {
  fetchCheckinData();
});
</script>

<style scoped>

.checkin-container {
  width: 100%;
}

.loading {
  text-align: center;
  padding: 60px 0;
}

.loading p {
  margin-top: 16px;
  color: var(--app-text-secondary);
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto;
  border: 3px solid var(--app-border-light);
  border-top-color: var(--app-color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #e74c3c;
  background: rgba(231, 76, 60, 0.08);
  padding: 14px 20px;
  border-radius: var(--app-radius-md);
  text-align: center;
  margin-bottom: 1rem;
  border: 1px solid rgba(231, 76, 60, 0.15);
}

.checkin-content {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 24px;
  align-items: start;
}

/* Status card */
.checkin-status-card {
  background: var(--app-bg-card);
  border-radius: var(--app-radius-xl);
  box-shadow: var(--app-shadow-card);
  border: 1px solid var(--app-border-subtle);
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.streak-section {
  text-align: center;
  padding: 20px;
  background: var(--app-gradient-primary);
  border-radius: var(--app-radius-lg);
  color: #fff;
  position: relative;
  overflow: hidden;
}

.streak-section::after {
  content: '';
  position: absolute;
  top: -50%;
  right: -30%;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  pointer-events: none;
}

.streak-icon {
  margin-bottom: 8px;
  opacity: 0.9;
}

.streak-days {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 2px;
}

.streak-value {
  font-size: 3rem;
  font-weight: 800;
  line-height: 1;
}

.streak-unit {
  font-size: 1.125rem;
  font-weight: 500;
  opacity: 0.85;
}

.streak-label {
  display: block;
  margin-top: 4px;
  font-size: 0.875rem;
  opacity: 0.85;
}

/* Progress */
.checkin-progress {
  padding: 0 4px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.progress-title {
  font-size: 0.8125rem;
  color: var(--app-text-secondary);
  font-weight: 500;
}

.progress-percent {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--app-color-primary);
}

.progress-track {
  height: 10px;
  background: var(--app-bg-soft);
  border-radius: 5px;
  overflow: visible;
  position: relative;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: var(--app-gradient-primary);
  border-radius: 5px;
  transition: width 0.6s ease;
  position: relative;
}

.progress-milestone {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  color: var(--app-color-primary);
  opacity: 0.4;
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.8125rem;
}

.progress-label {
  color: var(--app-text-secondary);
}

.progress-reward {
  color: var(--app-color-primary);
  font-weight: 500;
}

/* Buttons */
.checkin-btn {
  width: 100%;
  padding: 14px;
  background: var(--app-gradient-primary);
  color: #fff;
  border: none;
  border-radius: var(--app-radius-lg);
  font-size: 1.0625rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(255, 126, 126, 0.35);
  font-family: inherit;
}

.checkin-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 126, 126, 0.45);
}

.checkin-btn:disabled {
  background: var(--app-bg-soft);
  color: var(--app-text-secondary);
  box-shadow: none;
  cursor: not-allowed;
}

.reward-section {
  margin-top: 16px;
  padding: 16px;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 126, 126, 0.1));
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: var(--app-radius-lg);
  text-align: center;
}

.reward-glow {
  color: #f1c40f;
  margin-bottom: 8px;
  animation: glow 2s ease-in-out infinite;
}

@keyframes glow {
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.1); }
}

.reward-section p {
  color: #e67e22;
  margin: 0 0 12px;
  font-weight: 500;
  font-size: 0.9375rem;
}

.claim-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  background: linear-gradient(135deg, #f1c40f, #f39c12);
  color: #fff;
  border: none;
  border-radius: var(--app-radius-lg);
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 4px 12px rgba(241, 196, 15, 0.35);
  font-family: inherit;
}

.claim-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(241, 196, 15, 0.45);
}

/* Calendar */
.calendar-section {
  background: var(--app-bg-card);
  border-radius: var(--app-radius-xl);
  box-shadow: var(--app-shadow-card);
  border: 1px solid var(--app-border-subtle);
  padding: 24px;
}

.month-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--app-border-light);
}

.nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--app-bg-soft);
  border: 1px solid var(--app-border-light);
  padding: 8px 16px;
  border-radius: var(--app-radius-md);
  color: var(--app-text-secondary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.nav-btn:hover {
  border-color: var(--app-color-primary);
  color: var(--app-color-primary);
  background: var(--app-color-primary-muted);
}

.current-month {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--app-text-primary);
  margin: 0;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-bottom: 20px;
}

.weekday {
  text-align: center;
  font-weight: 600;
  color: var(--app-text-muted);
  padding: 10px 0;
  font-size: 0.875rem;
}

.date {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--app-bg-soft);
  border-radius: var(--app-radius-md);
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 0.9375rem;
  color: var(--app-text-primary);
  gap: 2px;
}

.date:hover:not(.empty):not(.future) {
  background: var(--app-color-primary-soft);
  transform: scale(1.05);
}

.date.empty {
  background: transparent;
  cursor: default;
}

.date.checked-in {
  background: linear-gradient(135deg, rgba(255, 126, 126, 0.12), rgba(139, 124, 247, 0.08));
  color: var(--app-color-primary);
  font-weight: 600;
}

.date.today {
  border: 2px solid var(--app-color-primary);
  font-weight: 700;
  background: var(--app-color-primary-muted);
}

.date.future {
  opacity: 0.4;
  cursor: default;
}

.checkin-mark {
  color: var(--app-color-primary);
  display: flex;
  align-items: center;
}

/* Stats */
.calendar-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid var(--app-border-light);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: var(--app-bg-soft);
  border-radius: var(--app-radius-md);
  border: 1px solid var(--app-border-subtle);
}

.stat-icon {
  width: 38px;
  height: 38px;
  border-radius: var(--app-radius-sm);
  background: var(--app-color-primary-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--app-color-primary);
  flex-shrink: 0;
}

.stat-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-label {
  font-size: 0.75rem;
  color: var(--app-text-muted);
}

.stat-value {
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text-primary);
}

@media (max-width: 768px) {
  .checkin-content {
    grid-template-columns: 1fr;
  }

  .calendar-grid {
    gap: 4px;
  }

  .date {
    font-size: 0.8125rem;
  }

  .calendar-stats {
    grid-template-columns: 1fr;
  }
}
</style>
